import { useCallback, useEffect, useState } from "react";
import type { AuthSessionResponse } from "@yagni/shared";
import { Button } from "../ui/button.js";
import { Spinner } from "../ui/spinner.js";
import { captureUiError } from "../sentry.js";

const PERMISSIONS: Record<string, string> = {
  "mcp:read": "Read Team context, Agent results, and work records",
  "mcp:invoke": "Request paid plan critiques and PR reviews",
  "mcp:manage": "Create Teams, engage Agents, and propose instruction changes",
  "mcp:decide": "Accept or decline supported decisions, including publishing reviews",
};
interface Consent {
  clientId: string;
  clientName: string;
  redirectUri: string;
  requestedScopes: string[];
  workspace: { id: string; name: string };
}
class ConsentFailure extends Error {
  constructor(readonly status: number, message: string) { super(message); }
}
const navigate = (url: string) => window.location.assign(url);

export function McpAuthorizePage({ requestId = new URLSearchParams(window.location.search).get("request") ?? "", onNavigate = navigate }: { requestId?: string; onNavigate?: (url: string) => void }) {
  const [session, setSession] = useState<AuthSessionResponse | null>(null);
  const [consent, setConsent] = useState<Consent | null>(null);
  const [scopes, setScopes] = useState<string[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const returnTo = `/mcp/authorize?request=${encodeURIComponent(requestId)}`;
  const signIn = `/auth?returnTo=${encodeURIComponent(returnTo)}`;

  const load = useCallback(async (signal?: AbortSignal) => {
    const sessionResponse = await fetch("/api/auth/session", { credentials: "include", signal });
    if (sessionResponse.status === 401) { onNavigate(signIn); return; }
    if (!sessionResponse.ok) throw new ConsentFailure(sessionResponse.status, "Could not load your workspace. Try again.");
    const current = await sessionResponse.json() as AuthSessionResponse;
    if (signal?.aborted) return;
    setSession(current);
    const response = await fetch(`/api/mcp/oauth/consent?request=${encodeURIComponent(requestId)}`, { credentials: "include", signal });
    if (!response.ok) throw new ConsentFailure(response.status, response.status === 404 ? "Agent connections are not available in this workspace yet." : "This connection request is unavailable. Reconnect from your coding client.");
    const next = await response.json() as Consent;
    if (signal?.aborted) return;
    setConsent(next);
    setScopes([]);
  }, [onNavigate, requestId, signIn]);

  function failure(caught: unknown) {
    if (!(caught instanceof ConsentFailure) || caught.status >= 500) {
      captureUiError(new Error("Worker connection failed"), { area: "Auth", operation: "mcp_consent", route: "/mcp/authorize", severity: "error", expected: false });
    }
    setError(caught instanceof ConsentFailure ? caught.message : "Could not connect. Check your connection and try again.");
  }

  useEffect(() => {
    const controller = new AbortController();
    if (!requestId) { setError("Open a connection request from your coding client."); setBusy(false); return; }
    void load(controller.signal).catch(caught => { if (!controller.signal.aborted) failure(caught); }).finally(() => { if (!controller.signal.aborted) setBusy(false); });
    return () => controller.abort();
  }, [load, requestId]);

  async function switchWorkspace(workspaceId: string) {
    setBusy(true); setError(null); setConsent(null); setScopes([]);
    try {
      const response = await fetch("/api/auth/switch-workspace", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ workspaceId }) });
      if (!response.ok) throw new ConsentFailure(response.status, "Could not switch workspace. If it requires SSO, sign in with that workspace and return to this request.");
      await load();
    } catch (caught) { failure(caught); } finally { setBusy(false); }
  }

  async function answer(approve: boolean) {
    if (!session || !consent) return;
    setBusy(true); setError(null);
    try {
      const response = await fetch("/api/mcp/oauth/consent", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ request: requestId, workspaceId: consent.workspace.id, scopes: approve ? scopes : [], approve }) });
      if (!response.ok) throw new ConsentFailure(response.status, "Could not save consent. Reload this page and review the request again.");
      const result = await response.json() as { redirectUrl: string };
      onNavigate(result.redirectUrl);
    } catch (caught) { failure(caught); setBusy(false); }
  }

  return <main className="min-h-screen px-6 py-12 text-text-primary" style={{ background: "var(--canvas-field)" }}>
    <div className="mx-auto max-w-xl rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] p-6 shadow-[var(--shadow-control)] sm:p-8">
      <p className="mb-6 font-[family-name:var(--font-ui)] text-sm">YAGNI</p>
      <h1 className="font-[family-name:var(--font-prose)] text-3xl">Connect your coding client</h1>
      <p className="mt-3 text-sm text-text-secondary">Choose the workspace and permissions this client may use. Your existing Team visibility and authority still apply.</p>
      {session && <div className="mt-6">
        <label htmlFor="mcp-workspace" className="block text-sm font-semibold">Workspace</label>
        <select id="mcp-workspace" className="mt-2 w-full rounded border border-[var(--print-border)] bg-[var(--print-bg)] p-3" disabled={busy} value={session.workspace.id} onChange={event => void switchWorkspace(event.target.value)}>
          {session.memberships.map(membership => <option key={membership.workspace.id} value={membership.workspace.id}>{membership.workspace.name}</option>)}
        </select>
      </div>}
      {consent && <>
        <div className="mt-6 rounded border border-[var(--print-border)] bg-[var(--print-bg)] p-4">
          <p className="font-semibold">{consent.clientName}</p>
          <p className="mt-1 text-xs text-text-secondary">This name is provided by the client. Confirm you started this connection.</p>
          <p className="mt-3 break-all font-mono text-xs">Return address: {consent.redirectUri}</p>
        </div>
        <fieldset className="mt-6 space-y-4" disabled={busy}>
          <legend className="mb-3 text-sm font-semibold">Allow this client to:</legend>
          {consent.requestedScopes.map(scope => <label key={scope} className="flex items-start gap-3 text-sm">
            <input type="checkbox" className="mt-1 accent-[var(--accent)]" checked={scopes.includes(scope)} onChange={event => setScopes(current => event.target.checked ? [...current, scope] : current.filter(value => value !== scope))} />
            <span>{PERMISSIONS[scope] ?? scope}</span>
          </label>)}
        </fieldset>
        <p className="mt-5 text-sm text-text-secondary">Paid requests use your workspace balance. Decisions accepted through this client are recorded as delegated actions.</p>
      </>}
      {busy && <div className="mt-5" role="status"><Spinner /><span className="sr-only">Loading connection</span></div>}
      {error && <div className="mt-5 text-sm" role="alert"><p>{error}</p><a href={signIn} className="mt-2 inline-block underline">Return to sign-in</a></div>}
      {session && <div className="mt-7 flex gap-3">
        <Button disabled={busy || !consent || scopes.length === 0} onClick={() => void answer(true)}>Connect</Button>
        <Button variant="secondary" disabled={busy || !consent} onClick={() => void answer(false)}>Cancel</Button>
      </div>}
    </div>
  </main>;
}
